import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  HeartPulse,
  Droplets,
  Thermometer,
  Footprints,
  Moon,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useAgentStore } from '../store/useAgentStore'
import { fetcher } from '../api/client'

interface HealthData {
  id: number
  heart_rate: number | null
  blood_oxygen: number | null
  blood_pressure_systolic: number | null
  blood_pressure_diastolic: number | null
  blood_glucose: number | null
  temperature: number | null
  steps: number | null
  sleep_hours: number | null
  recorded_at: string
}

interface HealthTrend {
  metric: string
  average: number
  trend: 'improving' | 'stable' | 'declining' | 'unknown'
}

export default function HealthMonitor() {
  const { t } = useTranslation()
  const { selectedElderlyId } = useAgentStore()
  const [days, setDays] = useState(7)
  const [metric, setMetric] = useState<'heart_rate' | 'blood_oxygen' | 'blood_glucose' | 'temperature' | 'steps' | 'sleep_hours'>('heart_rate')

  const { data: vitals, isLoading } = useQuery<HealthData>({
    queryKey: ['health', 'latest', selectedElderlyId],
    queryFn: () => fetcher(`/health/latest/${selectedElderlyId}`),
    enabled: !!selectedElderlyId,
    refetchInterval: 30000, // Refresh every 30s
  })

  const { data: trends } = useQuery<HealthTrend[]>({
    queryKey: ['health', 'trends', selectedElderlyId, days],
    queryFn: () => fetcher(`/health/trends/${selectedElderlyId}?days=${days}`),
    enabled: !!selectedElderlyId,
  })

  const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
  const { data: history } = useQuery<HealthData[]>({
    queryKey: ['health', 'history', selectedElderlyId, days],
    queryFn: () =>
      fetcher(`/health/data/${selectedElderlyId}?limit=500&start_date=${startDate}`),
    enabled: !!selectedElderlyId,
  })

  const chartMetrics = [
    { key: 'heart_rate' as const, label: t('health.heart_rate'), color: '#e11d48', unit: 'bpm' },
    { key: 'blood_oxygen' as const, label: t('health.blood_oxygen'), color: '#0284c7', unit: '%' },
    { key: 'blood_glucose' as const, label: t('health.blood_glucose'), color: '#d97706', unit: 'mg/dL' },
    { key: 'temperature' as const, label: t('health.temperature'), color: '#059669', unit: '°C' },
    { key: 'steps' as const, label: t('health.steps'), color: '#7c3aed', unit: '' },
    { key: 'sleep_hours' as const, label: t('health.sleep'), color: '#4f46e5', unit: 'h' },
  ]
  const activeMetric = chartMetrics.find((m) => m.key === metric)!

  // Chronological order; drop rows without a value for the selected metric
  const chartData = (history ?? [])
    .filter((d) => d[metric] !== null && d[metric] !== undefined)
    .map((d) => ({ at: d.recorded_at, value: d[metric] as number }))
    .reverse()

  const vitalsDisplay = [
    {
      label: t('health.heart_rate'),
      value: vitals?.heart_rate,
      unit: 'bpm',
      icon: HeartPulse,
      normal: [60, 100],
      color: 'text-rose-600 bg-rose-50 dark:bg-rose-900/20',
    },
    {
      label: t('health.blood_oxygen'),
      value: vitals?.blood_oxygen,
      unit: '%',
      icon: Droplets,
      normal: [95, 100],
      color: 'text-sky-600 bg-sky-50 dark:bg-sky-900/20',
    },
    {
      label: t('health.temperature'),
      value: vitals?.temperature,
      unit: '°C',
      icon: Thermometer,
      normal: [36.0, 37.5],
      color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20',
    },
    {
      label: t('health.steps'),
      value: vitals?.steps,
      unit: '',
      icon: Footprints,
      normal: [3000, 10000],
      color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20',
    },
    {
      label: t('health.sleep'),
      value: vitals?.sleep_hours,
      unit: 'h',
      icon: Moon,
      normal: [6, 9],
      color: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20',
    },
    {
      label: t('health.blood_pressure'),
      value: vitals?.blood_pressure_systolic
        ? `${vitals.blood_pressure_systolic}/${vitals.blood_pressure_diastolic}`
        : null,
      unit: 'mmHg',
      icon: Activity,
      normal: [90, 140],
      color: 'text-violet-600 bg-violet-50 dark:bg-violet-900/20',
    },
  ]

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp size={20} className="text-success-600" />
      case 'declining':
        return <TrendingDown size={20} className="text-danger-600" />
      default:
        return <Minus size={20} className="text-gray-400" />
    }
  }

  if (!selectedElderlyId) {
    return (
      <div className="text-center py-20 text-gray-500 dark:text-gray-400 text-xl">
        {t('health.no_elderly_selected')}
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('health.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            {t('health.subtitle')}
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="input-field w-40"
        >
          <option value={1}>{t('time.last_24h')}</option>
          <option value={7}>{t('time.last_7d')}</option>
          <option value={30}>{t('time.last_30d')}</option>
          <option value={90}>{t('time.last_90d')}</option>
        </select>
      </div>

      {/* Vitals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {vitalsDisplay.map((vital) => (
          <div key={vital.label} className="card">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl ${vital.color}`}>
                <vital.icon size={24} />
              </div>
              {trends?.find((t) => t.metric === vital.label) && (
                <div className="flex items-center gap-1">
                  {getTrendIcon(trends.find((t) => t.metric === vital.label)!.trend)}
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{vital.label}</p>
            <p className="text-4xl font-bold text-gray-900 dark:text-white">
              {isLoading ? '...' : vital.value ?? '--'}
              <span className="text-lg font-normal text-gray-500 ml-1">{vital.unit}</span>
            </p>
          </div>
        ))}
      </div>

      {/* Trends Chart */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('health.trends_chart')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {chartMetrics.map((m) => (
              <button
                key={m.key}
                onClick={() => setMetric(m.key)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                  metric === m.key
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
                style={metric === m.key ? { backgroundColor: m.color } : undefined}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {chartData.length === 0 ? (
          <div className="h-80 flex items-center justify-center bg-gray-50 dark:bg-gray-700/30 rounded-2xl">
            <p className="text-gray-400 text-lg">{t('health.no_data')}</p>
          </div>
        ) : (
          <div className="h-80 -ml-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 16, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.2} />
                <XAxis
                  dataKey="at"
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  tickFormatter={(v: string) =>
                    new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                  }
                  tickLine={false}
                  axisLine={{ stroke: '#9ca3af', strokeOpacity: 0.3 }}
                />
                <YAxis
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                  domain={['auto', 'auto']}
                />
                <Tooltip
                  formatter={(value: number) => [
                    `${value} ${activeMetric.unit}`.trim(),
                    activeMetric.label,
                  ]}
                  labelFormatter={(v: string) =>
                    new Date(v).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })
                  }
                  contentStyle={{
                    borderRadius: 12,
                    border: '1px solid rgba(156, 163, 175, 0.3)',
                    background: 'rgba(31, 41, 55, 0.92)',
                    color: '#f9fafb',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={activeMetric.color}
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: activeMetric.color }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  )
}